import React from "react";
import {
  ResponsiveContainer,
  LineChart,
  Line,
  AreaChart,
  Area,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  Tooltip,
} from "recharts";

export const C = {
  primary: "#2563eb",
  green: "#10b981",
  amber: "#f59e0b",
  red: "#dc2626",
  cyan: "#0ea5e9",
  purple: "#8b5cf6",
  grey: "#94a3b8",
};

export const STATUS_COLOR = { green: C.green, yellow: C.amber, blue: C.cyan, red: C.red };

export const SS_STAGE_COLOR = {
  Referral: "#64748b",
  "SS Schedule": "#2563eb",
  "SS Appointment Confirmation": "#3b82f6",
  "SS Facilitation": "#0ea5e9",
  "SS Follow up": "#14b8a6",
  "SS Reports": "#10b981",
};

export const TT_STAGE_COLOR = {
  "SS Feedback": "#8b5cf6",
  "TT Schedule": "#2563eb",
  "TT Appointment Confirmation": "#3b82f6",
  "TT Facilitation": "#0ea5e9",
  "TT Next day FU": "#14b8a6",
  "TT Report": "#10b981",
  "TT Feedback": "#059669",
};

const Tip = ({ active, payload, label, suffix = "" }) =>
  active && payload?.length && payload[0].value !== null ? (
    <div className="rounded-md border bg-white dark:bg-slate-800 dark:border-slate-700 px-2 py-1 text-[11px] shadow-md">
      <p className="font-medium">{payload[0].payload?.label || label}</p>
      <p className="text-muted-foreground">
        {payload[0].value}
        {suffix}
      </p>
    </div>
  ) : null;

/** Daily trend on the pace cards — flat line when nothing has been logged yet. */
export const Sparkline = ({ data, color = C.primary, suffix = "" }) => (
  <div className="h-12 -mx-1" aria-hidden="true">
    <ResponsiveContainer width="100%" height="100%" minWidth={0} minHeight={0}>
      <LineChart data={data} margin={{ top: 4, right: 4, bottom: 0, left: 4 }}>
        <Tooltip content={<Tip suffix={suffix} />} />
        <Line
          type="monotone"
          dataKey="value"
          stroke={color}
          strokeWidth={2}
          dot={{ r: 2, fill: color }}
          activeDot={{ r: 4 }}
          connectNulls
          isAnimationActive={false}
        />
      </LineChart>
    </ResponsiveContainer>
  </div>
);

export const AreaTrend = ({ data, color = C.primary, height = 72 }) => (
  <div style={{ height }} className="-mx-1">
    <ResponsiveContainer width="100%" height="100%" minWidth={0} minHeight={0}>
      <AreaChart data={data} margin={{ top: 6, right: 4, bottom: 0, left: 4 }}>
        <defs>
          <linearGradient id="trendFill" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={color} stopOpacity={0.35} />
            <stop offset="100%" stopColor={color} stopOpacity={0.02} />
          </linearGradient>
        </defs>
        <Tooltip content={<Tip />} />
        <Area
          type="monotone"
          dataKey="value"
          stroke={color}
          strokeWidth={2}
          fill="url(#trendFill)"
          isAnimationActive={false}
        />
      </AreaChart>
    </ResponsiveContainer>
  </div>
);

/** Vertical bars by rep, each bar clickable. */
export const RepBars = ({ data, onPick, palette = [C.green, C.primary, C.amber, C.purple, C.cyan] }) => (
  <div className="h-28">
    <ResponsiveContainer width="100%" height="100%" minWidth={0} minHeight={0}>
      <BarChart data={data} margin={{ top: 8, right: 4, bottom: 0, left: 4 }}>
        <XAxis
          dataKey="short"
          tick={{ fontSize: 10, fill: C.grey }}
          axisLine={false}
          tickLine={false}
          interval={0}
        />
        <Tooltip content={<Tip />} cursor={{ fill: "rgba(37,99,235,0.06)" }} />
        <Bar dataKey="value" radius={[4, 4, 0, 0]} maxBarSize={44} onClick={onPick} isAnimationActive={false} cursor="pointer">
          {data.map((entry, index) => (
            <Cell key={entry.label} fill={palette[index % palette.length]} />
          ))}
        </Bar>
      </BarChart>
    </ResponsiveContainer>
  </div>
);

/** Doctor status mix — slices drill through to that status. */
export const StatusDonut = ({ slices, total, onPick }) => {
  const live = slices.filter((s) => s.count > 0);
  return (
    <div className="flex items-center gap-3">
      <div className="h-28 w-28 shrink-0 relative">
        {live.length === 0 ? (
          <div className="absolute inset-0 rounded-full border-4 border-dashed border-muted" />
        ) : (
          <ResponsiveContainer width="100%" height="100%" minWidth={0} minHeight={0}>
            <PieChart>
              <Pie
                data={live}
                dataKey="count"
                nameKey="label"
                innerRadius={34}
                outerRadius={54}
                paddingAngle={2}
                isAnimationActive={false}
                onClick={(entry) => onPick?.(entry?.payload?.status || entry?.status)}
                cursor="pointer"
              >
                {live.map((slice) => (
                  <Cell key={slice.status} fill={STATUS_COLOR[slice.status]} stroke="none" />
                ))}
              </Pie>
              <Tooltip
                content={({ active, payload }) =>
                  active && payload?.length ? (
                    <div className="rounded-md border bg-white dark:bg-slate-800 dark:border-slate-700 px-2 py-1 text-[11px] shadow-md">
                      {payload[0].payload.label}: {payload[0].value}
                    </div>
                  ) : null
                }
              />
            </PieChart>
          </ResponsiveContainer>
        )}
        <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
          <span className="text-lg font-bold leading-none">{total}</span>
          <span className="text-[9px] text-muted-foreground">doctors</span>
        </div>
      </div>
      <ul className="space-y-1 text-[11px] min-w-0">
        {slices.map((slice) => (
          <li key={slice.status}>
            <button
              type="button"
              onClick={() => onPick?.(slice.status)}
              className="flex items-center gap-1.5 hover:text-[#2563eb] transition-colors text-left"
              data-testid={`doctor-status-${slice.status}`}
            >
              <span className="w-2 h-2 rounded-full shrink-0" style={{ background: STATUS_COLOR[slice.status] }} />
              <span className="truncate">{slice.label}</span>
              <span className="font-semibold tabular-nums">{slice.count}</span>
              <span className="text-muted-foreground">({slice.percent}%)</span>
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
};

/** Half-circle coverage gauge with the target marked on the arc. */
export const Gauge = ({ percent, target, color = C.green }) => {
  const radius = 46;
  const length = Math.PI * radius;
  const clamped = Math.max(0, Math.min(100, percent || 0));
  const markerAngle = Math.PI * (1 - Math.min(100, target) / 100);
  return (
    <div className="relative w-full flex justify-center">
      <svg viewBox="0 0 120 68" className="w-40 h-[68px]" role="img" aria-label={`${percent}% coverage`}>
        <path d="M14 60 A46 46 0 0 1 106 60" fill="none" stroke="currentColor" className="text-muted" strokeWidth="10" strokeLinecap="round" />
        <path
          d="M14 60 A46 46 0 0 1 106 60"
          fill="none"
          stroke={color}
          strokeWidth="10"
          strokeLinecap="round"
          strokeDasharray={`${(clamped / 100) * length} ${length}`}
        />
        <line
          x1={60 + Math.cos(markerAngle) * (radius - 9)}
          y1={60 - Math.sin(markerAngle) * (radius - 9)}
          x2={60 + Math.cos(markerAngle) * (radius + 9)}
          y2={60 - Math.sin(markerAngle) * (radius + 9)}
          stroke={C.grey}
          strokeWidth="2"
        />
      </svg>
      <span className="absolute bottom-0 text-[10px] text-muted-foreground">target {target}%</span>
    </div>
  );
};

/** Circular progress against last month's total. */
export const Ring = ({ value, benchmark, color = C.green }) => {
  const radius = 26;
  const circumference = 2 * Math.PI * radius;
  const ratio = benchmark > 0 ? Math.min(value / benchmark, 1) : 0;
  return (
    <div className="flex items-center gap-3">
      <svg viewBox="0 0 64 64" className="w-16 h-16 shrink-0 -rotate-90" role="img" aria-label={`${value} of ${benchmark}`}>
        <circle
          cx="32"
          cy="32"
          r={radius}
          fill="none"
          stroke="currentColor"
          className="text-muted"
          strokeWidth="7"
          strokeDasharray={benchmark > 0 ? undefined : "3 5"}
        />
        {benchmark > 0 && (
          <circle
            cx="32"
            cy="32"
            r={radius}
            fill="none"
            stroke={color}
            strokeWidth="7"
            strokeLinecap="round"
            strokeDasharray={`${ratio * circumference} ${circumference}`}
          />
        )}
      </svg>
      <p className="text-[11px] text-muted-foreground leading-snug">
        {benchmark > 0 ? (
          <>
            <span className="font-semibold text-foreground">{Math.round(ratio * 100)}%</span> of last month's{" "}
            {benchmark}
          </>
        ) : (
          "No completions last month to measure against"
        )}
      </p>
    </div>
  );
};

/** Segmented horizontal bar — every segment is a drill-down. */
export const SegmentBar = ({ segments, onPick, testId }) => {
  const total = segments.reduce((sum, s) => sum + s.value, 0);
  return (
    <div className="space-y-2" data-testid={testId}>
      <div className="flex h-2.5 rounded-full overflow-hidden bg-muted">
        {total === 0 ? (
          <div className="w-full bg-muted" />
        ) : (
          segments
            .filter((s) => s.value > 0)
            .map((segment) => (
              <button
                key={segment.label}
                type="button"
                title={`${segment.label}: ${segment.value}`}
                onClick={() => onPick?.(segment)}
                style={{ width: `${(segment.value / total) * 100}%`, background: segment.color }}
                className="h-full transition-opacity hover:opacity-80"
                aria-label={`${segment.label}: ${segment.value}`}
              />
            ))
        )}
      </div>
      <div className="flex flex-wrap gap-x-3 gap-y-1 text-[11px]">
        {segments.map((segment) => (
          <button
            key={segment.label}
            type="button"
            onClick={() => onPick?.(segment)}
            className="inline-flex items-center gap-1.5 hover:text-[#2563eb] transition-colors"
            data-testid={segment.testId}
          >
            <span className="w-2 h-2 rounded-full" style={{ background: segment.color }} />
            {segment.label}
            <span className="font-semibold tabular-nums">{segment.value}</span>
          </button>
        ))}
      </div>
    </div>
  );
};

/** Ranked list as proportional bars — used for top doctors and per-rep splits. */
export const RankBars = ({ items, color = C.primary, onPick, testId }) => {
  const max = Math.max(1, ...items.map((item) => item.value));
  return (
    <ul className="space-y-1.5" data-testid={testId}>
      {items.map((item) => (
        <li key={item.label}>
          <button
            type="button"
            onClick={() => onPick?.(item)}
            className="w-full text-left group"
            title={`${item.label}: ${item.value}`}
            data-testid={item.testId}
          >
            <div className="flex items-baseline justify-between gap-2 text-[11px]">
              <span className="truncate group-hover:text-[#2563eb] transition-colors">{item.label}</span>
              <span className="font-semibold tabular-nums">{item.value}</span>
            </div>
            <div className="h-1.5 rounded-full bg-muted overflow-hidden mt-0.5">
              <div
                className="h-full rounded-full transition-all duration-500"
                style={{ width: `${(item.value / max) * 100}%`, background: item.color || color }}
              />
            </div>
          </button>
        </li>
      ))}
    </ul>
  );
};

/** Two-step funnel with the drop-off called out. */
export const Funnel = ({ top, bottom, dropoff, percent }) => (
  <div className="space-y-1.5">
    {[
      { label: "Sleep study done", value: top, width: 100, color: C.primary },
      { label: "Titration done", value: bottom, width: Math.max(percent || 0, top ? 6 : 0), color: C.green },
    ].map((step) => (
      <div key={step.label} className="space-y-0.5">
        <div className="flex justify-between text-[11px]">
          <span>{step.label}</span>
          <span className="font-semibold tabular-nums">{step.value}</span>
        </div>
        <div
          className="h-5 rounded transition-all duration-500 mx-auto"
          style={{ width: `${Math.min(step.width, 100)}%`, background: step.color, opacity: step.value ? 1 : 0.25 }}
        />
      </div>
    ))}
    <p className="text-[11px] text-muted-foreground pt-0.5">
      {dropoff} diagnosed patient{dropoff === 1 ? "" : "s"} not yet on therapy
    </p>
  </div>
);
